import { IconTerminal2 } from "@tabler/icons-react";
import { useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import { declineSetup, installCommand } from "../api";
import { setupWays, thisDevice, type Way } from "../device";
import { conn } from "../net/connection";
import { holdConnection } from "../net/live";
import type { Session } from "../net/protocol";
import { refreshMe } from "../session";
import { newSessionDims } from "../term/manager";
import { Link, navigate } from "../router";
import { AppDownload, type AppPlatform } from "../ui/AppDownload";
import { errMsg, store, toast, useStore, type Me } from "../store";
import { CodeRow } from "../ui/CodeRow";
import "./cli.css";

/** The prompt that has a coding agent finish what setup couldn't (hub /agent-setup; Go's setup.AgentPrompt). */
export const agentPrompt = () =>
  `Follow ${location.origin}/agent-setup to finish setting up this cloud machine.`;

/** The machine's `setup.finish`: what's left, whether to offer it, and the coding agents that can do it, best first. */
export type Finish = {
  todo: number;
  left: string[];
  ask: boolean;
  prompt: string;
  session: string;
  agents: { id: string; name: string; command: string[]; signIn?: boolean }[];
};

/** Asks the machine what's left and who can finish it; null until it says. */
export function useFinish(on: boolean): [Finish | null, () => void] {
  const [finish, setFinish] = useState<Finish | null>(null);
  const [again, setAgain] = useState(0);
  useEffect(() => {
    if (!on) return;
    const release = holdConnection();
    let alive = true;
    conn
      .control<Finish>("setup.finish")
      .then((f) => alive && setFinish(f))
      .catch(() => {}); // it's away: the prompt still works
    return () => {
      alive = false;
      release();
    };
  }, [on, again]);
  return [finish, () => setAgain((n) => n + 1)];
}

/** Starts a coding agent on what's left in a session the workspace opens (or opens the one already at it). */
export async function startFinish(f: Finish, agent: Finish["agents"][number]) {
  try {
    const running = store
      .get()
      .sessions.find((s) => s.name === f.session && s.status !== "exited");
    const id =
      running?.id ??
      (
        await conn.control<{ session: Session }>("create", {
          name: f.session,
          command: agent.command,
          ...newSessionDims(),
        })
      ).session.id;
    navigate(`/app?session=${id}`);
  } catch (e) {
    toast({
      title: `Couldn’t start ${agent.name}`,
      body: errMsg(e),
      kind: "error",
    });
  }
}

/** "Not now" lasts this visit (a reload asks again); "Don't ask again" is kept where the offer lives. */
const notNow = new Set<"setup" | "finish">();
const notNowSubs = new Set<() => void>();
export function hideForVisit(what: "setup" | "finish") {
  notNow.add(what);
  notNowSubs.forEach((f) => f());
}
/** Whether the person said Not now to this offer on this visit. */
export function useNotNow(what: "setup" | "finish"): boolean {
  return useSyncExternalStore(
    (f) => {
      notNowSubs.add(f);
      return () => notNowSubs.delete(f);
    },
    () => notNow.has(what),
  );
}

/** Magic Setup still to answer: neither run nor declined (spec "Dashboard: onboarding first"). */
export const setupOpen = (me: Me) => me.setup.offer === "ask";

/** Don't ask again to Magic Setup, kept for the account. */
export async function declineMagicSetup() {
  try {
    await declineSetup();
    await refreshMe();
  } catch (e) {
    toast({ title: "Couldn’t save that", body: errMsg(e), kind: "error" });
  }
}

/** Not now and Don't ask again, the same two answers every app gives (spec "Asking until it's done or declined"). */
function OfferAnswers({
  id,
  onNotNow,
  onNever,
}: {
  id: string;
  onNotNow: () => void;
  onNever: () => void;
}) {
  return (
    <div className="start-answers">
      <button
        className="link-btn"
        onClick={onNotNow}
        data-testid={`${id}-not-now`}
      >
        Not now
      </button>
      <button
        className="link-btn"
        onClick={onNever}
        data-testid={`${id}-never`}
      >
        Don’t ask again
      </button>
    </div>
  );
}

/**
 * "Get started" (spec "New-user journey"): the machine (it fires up at sign-up), bringing the
 * laptop's setup over (the Mac app, `cmx` in your own terminal, or just the browser), and a
 * coding agent finishing the rest. Magic Setup and finishing are offered until done or
 * declined (the hub's setup.offer, the machine's setup.finish ask); Not now hides one for
 * this visit. The card goes once nothing's left to show.
 */
export function GetStartedCard({ me, aside }: { me: Me; aside?: ReactNode }) {
  const status = useStore((s) => s.me?.machine?.status);
  const ready = me.journey.includes("machine") || status === "running";
  const setupLater = useNotNow("setup");
  const finishLater = useNotNow("finish");
  const askSetup = setupOpen(me) && !setupLater;
  const [finish, reloadFinish] = useFinish(ready);
  const askFinish = !!finish?.ask && !finishLater;
  const [starting, setStarting] = useState(false);
  const running = useStore((st) =>
    finish
      ? st.sessions.some(
          (x) => x.name === finish.session && x.status !== "exited",
        )
      : false,
  );
  if (ready && !askSetup && !askFinish) return null;
  const [best, ...others] = finish?.agents ?? [];
  const left = finish?.todo ?? 0;
  const start = (a: Finish["agents"][number]) => {
    setStarting(true);
    void startFinish(finish!, a).finally(() => setStarting(false));
  };
  const declineFinish = async () => {
    try {
      await conn.control("setup.finish.decline");
      reloadFinish();
    } catch (e) {
      toast({ title: "Couldn’t save that", body: errMsg(e), kind: "error" });
    }
  };
  // One frame for every step (the same as the Next step card): the step's title and lead on the left, the machine on the right, its answers in cells beneath.
  if (askSetup) return <SetupHero me={me} aside={aside} />;
  if (askFinish) {
    return (
      <HeroFrame
        id="start"
        testid="get-started"
        title="Let a coding agent finish setting up"
        lead={
          <>
            {left} thing{left === 1 ? "" : "s"} setup couldn’t do by {left === 1 ? "itself" : "themselves"}:{" "}
            {finish!.left.slice(0, 3).join(" · ")}
            {left > 3 ? ` and ${left - 3} more` : ""}.{" "}
            {running ? `A coding agent is on it in ${finish!.session}.` : "It takes one prompt."}
          </>
        }
        aside={aside}
        answers={{ id: "start-finish", onNotNow: () => hideForVisit("finish"), onNever: () => void declineFinish() }}
      >
        <div className="start-ways is-hero" data-testid="start-agent">
          {best && (
            <div className="start-way is-recommended" data-testid="start-finish">
              <span className="start-way-title">
                {running ? `${best.name} is working` : `${best.name} can finish it`}{" "}
                <span className="start-badge">Recommended</span>
              </span>
              <span className="muted">
                {running
                  ? "It’s running in a session on your cloud machine."
                  : `It does ${left === 1 ? "it" : "them"} in a new session${best.signIn ? ", and asks you to sign in first" : ""}.`}
              </span>
              <div className="start-finish">
                <button className="btn btn-primary" disabled={starting} onClick={() => start(best)} data-testid="start-finish-agent">
                  {running ? `Open ${finish!.session}` : `Finish with ${best.name}`}
                </button>
                {!running &&
                  others.slice(0, 2).map((a) => (
                    <button key={a.id} className="link-btn" disabled={starting} onClick={() => start(a)} data-testid={`start-finish-${a.id}`}>
                      or {a.name}
                    </button>
                  ))}
              </div>
            </div>
          )}
          <div className="start-way">
            <span className="start-way-title">Ask any coding agent</span>
            <span className="muted">In a session on your cloud machine, paste this prompt.</span>
            <CodeRow text={agentPrompt()} codeTestid="start-agent-prompt" copyTestid="start-copy-prompt" />
          </div>
        </div>
      </HeroFrame>
    );
  }
  // Waiting for the machine: the same frame, no cells yet.
  return (
    <HeroFrame
      id="start"
      testid="get-started"
      kicker="Getting ready"
      title={me.machine ? "Your cloud machine is starting" : "Your cloud machine is on its way"}
      lead="It’s always on and faster than this computer, and it keeps working while this computer sleeps. This takes a moment."
      aside={aside}
    />
  );
}

/**
 * The one frame every "next step" on the dashboard shares: kicker, title and lead on the left, the machine
 * on the right, the step's ways in hairline-ruled cells beneath, and Not now / Don't ask again as a footer.
 */
function HeroFrame({
  id,
  testid,
  kicker = "Next step",
  title,
  lead,
  aside,
  answers,
  children,
}: {
  id: string;
  testid: string;
  kicker?: string;
  title: string;
  lead: ReactNode;
  aside?: ReactNode;
  answers?: { id: string; onNotNow: () => void; onNever: () => void };
  children?: ReactNode;
}) {
  return (
    <section className="card start-hero" aria-labelledby={`${id}-hero-title`} data-testid={testid}>
      <div className="start-hero-head">
        <div className="start-hero-copy">
          <span className="start-hero-kicker">{kicker}</span>
          <h2 id={`${id}-hero-title`} className="start-hero-title">
            {title}
          </h2>
          <p className="start-hero-lead">{lead}</p>
        </div>
        {aside && <div className="start-hero-aside">{aside}</div>}
      </div>
      {children}
      {answers && <OfferAnswers {...answers} />}
    </section>
  );
}

/** A small window drawing for a way in: a terminal, a browser, the Mac app (traffic lights, a dock) or the Windows app
 *  (its controls at the right, a taskbar). Stone strokes, one blue accent. */
export function WayArt({ kind }: { kind: "terminal" | "browser" | "app" | "windows" }) {
  if (kind === "windows")
    return (
      <svg className="way-art" width="56" height="40" viewBox="0 0 56 40" fill="none" aria-hidden="true" focusable="false">
        <rect x="0.5" y="0.5" width="55" height="31" rx="4.5" fill="#fff" stroke="#A8A29E" />
        <path d="M1 8.5h54" stroke="#D6D3D1" />
        <path d="M38.5 4.5h3" stroke="#A8A29E" strokeLinecap="round" />
        <rect x="44.5" y="3" width="3" height="3" rx="0.5" stroke="#A8A29E" />
        <path d="M50.5 3l3 3M53.5 3l-3 3" stroke="#A8A29E" strokeLinecap="round" />
        <path d="M18 9v22" stroke="#D6D3D1" />
        <rect x="4" y="12" width="11" height="5" rx="1.5" fill="#EFF6FF" stroke="#84CAFF" />
        <path d="M5 21.5h8M5 26h6" stroke="#D6D3D1" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M23 14l3.5 2.7L23 19.4" stroke="#1570EF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M29 19.5h8" stroke="#A8A29E" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M23 25h24M23 28.5h14" stroke="#D6D3D1" strokeWidth="1.6" strokeLinecap="round" />
        <rect x="0.5" y="35.5" width="55" height="4" rx="1" fill="#F5F5F4" stroke="#D6D3D1" />
        <rect x="20.5" y="36.75" width="2.5" height="1.5" rx="0.5" fill="#A8A29E" />
        <rect x="26.75" y="36.75" width="2.5" height="1.5" rx="0.5" fill="#1570EF" />
        <rect x="33" y="36.75" width="2.5" height="1.5" rx="0.5" fill="#A8A29E" />
      </svg>
    );
  if (kind === "app")
    return (
      <svg className="way-art" width="56" height="40" viewBox="0 0 56 40" fill="none" aria-hidden="true" focusable="false">
        <rect x="0.5" y="0.5" width="55" height="32" rx="6.5" fill="#fff" stroke="#A8A29E" />
        <path d="M1 9.5h54" stroke="#D6D3D1" />
        <circle cx="6" cy="5" r="1.4" fill="#D6D3D1" />
        <circle cx="11" cy="5" r="1.4" fill="#D6D3D1" />
        <circle cx="16" cy="5" r="1.4" fill="#D6D3D1" />
        <path d="M19 10v22" stroke="#D6D3D1" />
        <rect x="4" y="13" width="12" height="5" rx="1.5" fill="#EFF6FF" stroke="#84CAFF" />
        <path d="M5 22.5h9M5 27h7" stroke="#D6D3D1" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M24 15l3.5 2.7L24 20.4" stroke="#1570EF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M30 20.5h8" stroke="#A8A29E" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M24 26h24M24 29.5h14" stroke="#D6D3D1" strokeWidth="1.6" strokeLinecap="round" />
        <rect x="15.5" y="35.5" width="25" height="4" rx="2" fill="#F5F5F4" stroke="#D6D3D1" />
        <circle cx="22" cy="37.5" r="1" fill="#A8A29E" />
        <circle cx="28" cy="37.5" r="1" fill="#1570EF" />
        <circle cx="34" cy="37.5" r="1" fill="#A8A29E" />
      </svg>
    );
  return (
    <svg className="way-art" width="56" height="40" viewBox="0 0 56 40" fill="none" aria-hidden="true" focusable="false">
      <rect x="0.5" y="0.5" width="55" height="39" rx="6.5" fill="#fff" stroke="#A8A29E" />
      <path d="M1 10.5h54" stroke="#D6D3D1" />
      <circle cx="6" cy="5.5" r="1.4" fill="#D6D3D1" />
      <circle cx="11" cy="5.5" r="1.4" fill="#D6D3D1" />
      <circle cx="16" cy="5.5" r="1.4" fill="#D6D3D1" />
      {kind === "terminal" && (
        <>
          <path d="M8 17l4 3-4 3" stroke="#1570EF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M15 24h8" stroke="#A8A29E" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M8 31h24M8 35h14" stroke="#D6D3D1" strokeWidth="1.6" strokeLinecap="round" />
        </>
      )}
      {kind === "browser" && (
        <>
          <rect x="22" y="3" width="28" height="5" rx="2.5" fill="#F5F5F4" stroke="#D6D3D1" />
          <rect x="6" y="16" width="44" height="9" rx="2" fill="#EFF6FF" stroke="#84CAFF" />
          <path d="M6 31h30M6 35h18" stroke="#D6D3D1" strokeWidth="1.6" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

/** The apps' cards: their name, the drawing, the badge when it's for another computer. */
const APP_WAY: Record<Exclude<Way, "cli">, { platform: AppPlatform; name: string; art: "app" | "windows"; other: string }> = {
  mac: { platform: "mac", name: "Cloud Machine for Mac", art: "app", other: "For Mac" },
  win: { platform: "windows", name: "Cloud Machine for Windows", art: "windows", other: "For Windows" },
};

/**
 * The ways to bring this computer's setup over, the one this device should take first and marked Recommended (spec
 * "Getting ready", its table): the Mac app on a Mac, the Windows app on Windows, your own terminal elsewhere; just
 * the browser last. hero: the dashboard's onboarding view, larger.
 */
function SetupWays({ me, hero = false }: { me: Me; hero?: boolean }) {
  const [device] = useState(thisDevice);
  const { ways, recommended: rec } = setupWays(device, me.apps);
  const install = installCommand();
  const card = (w: Way) => {
    const badge = (other: string | false) =>
      rec === w ? <span className="start-badge">Recommended</span> : other && <span className="start-badge is-quiet">{other}</span>;
    if (w === "cli")
      return (
        <div key="cli" className={`start-way ${rec === "cli" ? "is-recommended" : ""}`} data-testid="start-way-cli">
          {hero && <WayArt kind="terminal" />}
          <span className="start-way-title">Your own terminal {badge(false)}</span>
          <span className="muted">
            Install, then run <code>cmx</code>: it sets your cloud machine up like
            this computer and opens a session.
            {device === "windows" && <> On Windows, in WSL (<code>wsl --install</code> first).</>}
          </span>
          <CodeRow text={install} codeTestid="start-install-cmd" copyTestid="start-copy-install" />
        </div>
      );
    const a = APP_WAY[w];
    const own = (w === "mac" && device === "mac") || (w === "win" && device === "windows");
    return (
      <div key={w} className={`start-way ${rec === w ? "is-recommended" : ""}`} data-testid={`start-way-${w === "mac" ? "app" : "windows"}`}>
        {hero && <WayArt kind={a.art} />}
        <span className="start-way-title">
          {a.name} {badge(!own && a.other)}
        </span>
        <span className="muted">
          Sets your cloud machine up, then it’s where you work: terminal, desktop
          and previews in one window.
        </span>
        <AppDownload platform={a.platform} className={`btn btn-primary ${hero ? "" : "btn-sm"}`} />
      </div>
    );
  };
  return (
    <div className={`start-ways ${hero ? "is-hero" : ""}`} data-testid="start-ways">
      {ways.map(card)}
      <div className="start-way" data-testid="start-way-web">
        {hero && <WayArt kind="browser" />}
        <span className="start-way-title">Just the browser</span>
        <span className="muted">
          Nothing to install. You can bring your setup over from a computer any
          time.
        </span>
        {hero ? (
          <Link to="/app" className="btn-tertiary" data-testid="start-open-workspace">
            <IconTerminal2 size={16} stroke={1.5} aria-hidden="true" />
            Open workspace
          </Link>
        ) : (
          <Link to="/app" className="btn btn-sm" data-testid="start-open-workspace">
            Open workspace
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * The dashboard while Magic Setup is still to answer (spec "Dashboard: onboarding first"): the
 * one next step, large. Not now shows the full dashboard for this visit; Don't ask again for good.
 */
export function SetupHero({ me, aside }: { me: Me; aside?: ReactNode }) {
  return (
    <HeroFrame
      id="start"
      testid="setup-hero"
      title="Bring this computer’s setup to your cloud machine"
      lead="Magic Setup moves your tools, shell, coding agents, sign-ins and projects over, so your cloud machine feels like this computer from the first command."
      aside={aside}
      answers={{ id: "start-setup", onNotNow: () => hideForVisit("setup"), onNever: () => void declineMagicSetup() }}
    >
      <SetupWays me={me} hero />
    </HeroFrame>
  );
}
